import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import moment from "moment-timezone";
import _ from "lodash";

import { useAppDispatch } from "../../../redux/useDispatch";
import { RootState } from "../../../redux/store";
import {
  getSUPAvailableData,
  getSUPAvailableTimeSlots,
} from "../util/orderDataStepHelper";
import {
  dateSelected,
  durationSelected,
  quantitySelected,
  timeSelected,
} from "../../../redux/slice/bookingCreation/bookingCreationSlice";

import BookingDatePicker from "../common/components/OrderDataStep/BookingDatePicker";
import BookingTimeSelect from "../common/components/OrderDataStep/BookingTimeSelect";
import BookingDurationSelect from "../common/components/OrderDataStep/BookingDurationSelect";
import BookingQuantitySelect from "../common/components/OrderDataStep/BookingQuantitySelect";
import LoaderOverlay from "../../layout/components/LoaderOverlay";
import { useBookingWindow } from "../common/hooks/useBookingWindow";
import { useSelectedLocationTimeZone } from "../../../util/locationTimeZone";

const OrderDataStep = () => {
  const { t } = useTranslation();
  // The tenant's configured booking horizon for this use case — the picker
  // used to cap dates at a hardcoded 18 months, which contradicted the one
  // month the availability request already asked for. One month is the
  // default here, so the effective behaviour is unchanged.
  const bookingWindow = useBookingWindow("sup");
  const dispatch = useAppDispatch();

  // The station's clock (#3219): slots are keyed in the location's zone.
  const timeZone = useSelectedLocationTimeZone();
  const groupAvailabilities = useSelector(
    (state: RootState) => state.bookingCreation.supAvailabilities
  );
  const orderData = useSelector(
    (state: RootState) => state.bookingCreation.orderData
  );

  const isLoading = _.isEmpty(groupAvailabilities);
  const hasAvailabilities = Boolean(groupAvailabilities);

  const availableDates = useMemo<string[]>(() => {
    if (!groupAvailabilities) return [];
    return Object.keys(groupAvailabilities).filter((dateKey) => {
      const timeSlots = groupAvailabilities[dateKey];
      return Object.values(timeSlots).some((slot) =>
        Object.values(slot).some((value) => value.count > 0)
      );
    });
  }, [groupAvailabilities]);

  const availableTimeSlots = useMemo<string[]>(() => {
    if (!groupAvailabilities || !orderData.date) return [];
    return getSUPAvailableTimeSlots(groupAvailabilities, orderData.date, timeZone);
  }, [groupAvailabilities, orderData.date, timeZone]);

  const { allDurations, allPriceMap, allLimitMap } = useMemo(() => {
    if (!groupAvailabilities || !orderData.date || !orderData.time) {
      return {
        allDurations: [] as number[],
        allPriceMap: {} as Record<number, number>,
        allLimitMap: {} as Record<number, number>,
      };
    }
    const { durations, priceMap, limitsMap } = getSUPAvailableData(
      groupAvailabilities,
      orderData.date,
      orderData.time,
      timeZone
    );
    return {
      allDurations: durations ?? [],
      allPriceMap: priceMap ?? {},
      allLimitMap: limitsMap ?? {},
    };
  }, [groupAvailabilities, orderData.date, orderData.time, timeZone]);

  const enableTimeSelect = hasAvailabilities && Boolean(orderData.date);
  const enableDurationSelect =
    hasAvailabilities && Boolean(orderData.date) && orderData.time !== "";
  const enableQuantitySelect =
    enableDurationSelect && Boolean(orderData.duration);

  const handleDateChange = (date: moment.Moment | null) => {
    dispatch(dateSelected({ date }));
  };

  const handleTimeChange = (time: string) => {
    dispatch(timeSelected({ time }));
  };

  return (
    <>
      {hasAvailabilities &&
        (_.isEmpty(groupAvailabilities) ? (
          <div className="px-4">
            <p className="text-base text-slate-900">
              {t("book_location.order_data.error.no_availabilities_for_group")}
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-4 mx-4">
            <BookingDatePicker
              value={orderData.date}
              onChange={handleDateChange}
              shouldDisableDate={() => false}
              availableDates={availableDates}
              maxDate={bookingWindow.latest}
              label={t("general.date")}
            />
            <BookingTimeSelect
              availableSlots={availableTimeSlots}
              value={orderData.time}
              onChange={handleTimeChange}
              availableDurations={allDurations}
              disabled={!enableTimeSelect}
              label={t("general.select")}
            />
            <BookingDurationSelect
              value={orderData.duration}
              onChange={(duration) =>
                dispatch(durationSelected({ duration }))
              }
              availableDurations={allDurations}
              disabled={!enableDurationSelect}
              prices={allPriceMap}
              label={t("general.duration")}
            />
            <BookingQuantitySelect
              value={orderData.quantity}
              onChange={(quantity) =>
                dispatch(quantitySelected({ quantity }))
              }
              limit={allLimitMap[orderData.duration] ?? 0}
              disabled={!enableQuantitySelect}
              label={t("general.quantity")}
            />
          </div>
        ))}
      <LoaderOverlay open={isLoading} />
    </>
  );
};

export default OrderDataStep;
